import { Link, useNavigate } from "@tanstack/react-router";
import {
  type MouseEvent,
  type ReactNode,
  type TouchEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import {
  affichageDeLecture,
  ErreurColoriage,
  FilColoriage,
  ImageVersion,
  IntrouvableColoriage,
  idValide,
  LIEN_BOUTON,
  LIEN_BOUTON_PLEIN,
  RelectureEchouee,
} from "./coloriage-commun";
import { BadgeJugement, PointsDesVersions } from "./coloriage-points";
import { messageDEchec, useChoisir, useJuger, usePageColoriage } from "./coloriage-requetes";
import {
  JUGEMENTS,
  LIBELLE_JUGEMENT,
  modeleLong,
  TOUCHE_JUGEMENT,
  titrePage,
} from "./coloriage-textes";
import type { Controle, Jugement, PageDetail, VersionDetail } from "./contract";
import { ConsoleShell } from "./shell";
import { Chargement } from "./states";

/**
 * Juger une page (`/coloriage/pages/$id?v=<numero>`), le coeur du module : une version en
 * grand, trois boutons, une note, le choix de la page, les controles du PC, les autres
 * versions et le prompt.
 *
 * Cinq regles tiennent la page :
 *
 * - **L'adresse dit la version affichee** (`v`, son numero dans la page). Sans `v`, la
 *   premiere version a juger, sinon la premiere ; l'adresse est alors completee, pour qu'un
 *   jugement qui ne mene nulle part ne fasse pas sauter l'ecran sur une autre version.
 * - **Apres un jugement, la prochaine version a juger** que rend le backend : meme page, on
 *   change de version ; autre page, on y va ; aucune, on reste.
 * - **Le clavier juge** (1, 2, 3 ; C choisit ; fleches gauche et droite), sauf quand le focus
 *   est dans un champ de saisie : taper « 1 » dans la note ne juge rien. Les chiffres sont lus
 *   aussi par leur touche physique (`Digit1`), pour un clavier AZERTY sans majuscule. A
 *   100 %, les fleches font defiler l'image agrandie : elles ne changent plus de version.
 * - **Une version sans son image ne se juge ni ne se choisit** : boutons eteints, touches
 *   sans effet, et la page dit que l'image n'est pas encore arrivee. Nicolas ne decide que
 *   sur ce qu'il a vu.
 * - **Sur telephone**, une colonne et la barre des boutons collee en bas de l'ecran
 *   (`coloriage-barre`, voir `theme.css`). La note passe au-dessus des boutons sur tous les
 *   ecrans : elle part avec le jugement, elle s'ecrit donc avant.
 */

type Mode = "une" | "cote";

/** Ce qu'une touche demande a la page. */
export type Action =
  | { readonly type: "juger"; readonly jugement: Jugement }
  | { readonly type: "choisir" }
  | { readonly type: "precedente" }
  | { readonly type: "suivante" };

const PAR_TOUCHE: Readonly<Record<string, Jugement>> = {
  "1": "bonne",
  "2": "defaut",
  "3": "a_jeter",
};

/** La touche physique : sur un AZERTY, « 1 » sans majuscule donne `&`, mais reste `Digit1`. */
const PAR_CODE: Readonly<Record<string, Jugement>> = {
  Digit1: "bonne",
  Numpad1: "bonne",
  Digit2: "defaut",
  Numpad2: "defaut",
  Digit3: "a_jeter",
  Numpad3: "a_jeter",
};

/** Vrai quand la frappe va a un champ de saisie : la page ne doit pas la prendre. */
export function estUnChamp(cible: EventTarget | null): boolean {
  if (!(cible instanceof HTMLElement)) return false;
  return (
    cible.isContentEditable === true ||
    cible instanceof HTMLInputElement ||
    cible instanceof HTMLTextAreaElement ||
    cible instanceof HTMLSelectElement
  );
}

/**
 * Traduit une frappe en action, ou `null` quand elle ne concerne pas la page : champ de
 * saisie, touche de commande (Ctrl+C copie, il ne choisit pas), ou touche maintenue (une
 * touche qui se repete jugerait plusieurs versions d'affilee). Image a 100 % (`zoom`), les
 * fleches restent au navigateur, qui fait defiler l'image agrandie.
 */
export function actionDeTouche(
  event: Pick<
    KeyboardEvent,
    "key" | "code" | "ctrlKey" | "metaKey" | "altKey" | "repeat" | "target" | "defaultPrevented"
  >,
  { zoom = false }: { readonly zoom?: boolean } = {},
): Action | null {
  if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return null;
  if (estUnChamp(event.target)) return null;
  if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
    if (zoom) return null;
    return { type: event.key === "ArrowLeft" ? "precedente" : "suivante" };
  }
  if (event.repeat) return null;
  const jugement = PAR_TOUCHE[event.key] ?? PAR_CODE[event.code];
  if (jugement !== undefined) return { type: "juger", jugement };
  if (event.key === "c" || event.key === "C") return { type: "choisir" };
  return null;
}

/**
 * La version a afficher : celle de l'adresse, sinon la premiere a juger (sans jugement, son
 * image arrivee), sinon la premiere sans jugement, sinon la premiere.
 */
export function versionAffichee(
  versions: readonly VersionDetail[],
  essai: number | undefined,
): VersionDetail | undefined {
  return (
    versions.find((version) => version.essai === essai) ??
    versions.find((version) => version.jugement === null && version.image) ??
    versions.find((version) => version.jugement === null) ??
    versions[0]
  );
}

/**
 * Un glisser horizontal franc, sur telephone : -1 vers la version precedente (le doigt va a
 * droite), 1 vers la suivante. Un geste court ou surtout vertical (on fait defiler la page)
 * ne compte pas.
 */
export function sensDuGlisser(
  depart: { x: number; y: number },
  arrivee: { x: number; y: number },
): -1 | 1 | null {
  const dx = arrivee.x - depart.x;
  const dy = arrivee.y - depart.y;
  if (Math.abs(dx) < 48 || Math.abs(dx) < 1.5 * Math.abs(dy)) return null;
  return dx < 0 ? 1 : -1;
}

export function ColoriagePagePage({ id, v }: { id: string; v: number | undefined }) {
  const valide = idValide(id);
  const lecture = usePageColoriage(id, valide);
  // Jamais l'ecran d'erreur et la page ensemble (`affichageDeLecture`).
  const affichage = affichageDeLecture(lecture);
  const detail = affichage.contenu ? lecture.data : undefined;

  return (
    <ConsoleShell connecte={affichage.contenu}>
      <FilColoriage
        cahier={
          detail === undefined ? undefined : { id: detail.cahier.id, titre: detail.cahier.titre }
        }
      >
        {detail === undefined ? null : titrePage(detail.page.rang, detail.page.titre)}
      </FilColoriage>
      {!valide ? <IntrouvableColoriage quoi={`la page ${id}`} /> : null}
      {valide && lecture.isPending ? <Chargement quoi="de la page" /> : null}
      {affichage.ecranDErreur ? (
        <ErreurColoriage error={lecture.error} quoi={`la page ${id}`} />
      ) : null}
      {affichage.bandeau ? <RelectureEchouee error={lecture.error} /> : null}
      {detail === undefined ? null : <Juger detail={detail} essai={v} key={detail.page.id} />}
    </ConsoleShell>
  );
}

function Juger({ detail, essai }: { detail: PageDetail; essai: number | undefined }) {
  const navigate = useNavigate();
  const { cahier, page, versions } = detail;
  const pageId = String(page.id);
  const juger = useJuger(pageId);
  const choisir = useChoisir(pageId);
  const [mode, setMode] = useState<Mode>("une");
  // Une note en cours d'ecriture par version : passer d'une version a l'autre ne la perd pas.
  const [brouillons, setBrouillons] = useState<Readonly<Record<number, string | undefined>>>({});
  const [echec, setEchec] = useState<string | null>(null);
  // La version montree a 100 %, par son identifiant : changer de version sort du zoom.
  const [zoomee, setZoomee] = useState<number | null>(null);

  const courante = versionAffichee(versions, essai);
  const clos = cahier.etat === "clos";
  const occupe = juger.isPending || choisir.isPending;
  const titre = titrePage(page.rang, page.titre);
  const cent = courante !== undefined && zoomee === courante.id;
  // Sans son image, une version ne se juge ni ne se choisit (voir l'en-tete du fichier).
  const decidable = courante?.image === true && !clos;

  // L'adresse porte toujours la version a l'ecran (voir l'en-tete du fichier).
  useEffect(() => {
    if (courante !== undefined && courante.essai !== essai) {
      void navigate({
        to: "/coloriage/pages/$id",
        params: { id: pageId },
        search: { v: courante.essai },
        replace: true,
      });
    }
  }, [courante, essai, navigate, pageId]);

  const afficher = (numero: number): void => {
    setEchec(null);
    setZoomee(null);
    void navigate({
      to: "/coloriage/pages/$id",
      params: { id: pageId },
      search: { v: numero },
      replace: true,
    });
  };

  const index = courante === undefined ? -1 : versions.indexOf(courante);
  const voisine = (pas: -1 | 1): void => {
    const cible = versions[index + pas];
    if (cible !== undefined) afficher(cible.essai);
  };

  const note = courante === undefined ? "" : (brouillons[courante.id] ?? courante.note ?? "");

  const jugerCourante = (jugement: Jugement): void => {
    if (courante === undefined || !decidable || occupe) return;
    setEchec(null);
    const propre = note.trim();
    juger.mutate(
      { versionId: courante.id, jugement, note: propre === "" ? null : propre },
      {
        onSuccess: (reponse) => {
          setBrouillons((avant) => ({ ...avant, [courante.id]: undefined }));
          const prochaine = reponse.prochaine_a_juger;
          if (prochaine === null) return;
          if (prochaine.page === page.id) {
            afficher(prochaine.essai);
            return;
          }
          void navigate({
            to: "/coloriage/pages/$id",
            params: { id: String(prochaine.page) },
            search: { v: prochaine.essai },
          });
        },
        onError: (error) => setEchec(messageDEchec(error)),
      },
    );
  };

  const basculerChoix = (): void => {
    if (courante === undefined || !decidable || occupe) return;
    setEchec(null);
    choisir.mutate(
      { version: page.choix_version === courante.id ? null : courante.id },
      { onError: (error) => setEchec(messageDEchec(error)) },
    );
  };

  // Changer d'affichage sort du zoom : l'image a 100 % n'est que dans "une a la fois".
  const changerDeMode = (nouveau: Mode): void => {
    setZoomee(null);
    setMode(nouveau);
  };

  // Le clavier : un seul ecouteur sur la fenetre, qui lit toujours l'etat du dernier rendu.
  const clavier = useRef<(event: KeyboardEvent) => void>(() => undefined);
  useEffect(() => {
    clavier.current = (event) => {
      // A 100 %, les fleches restent au navigateur : elles font defiler l'image agrandie.
      const action = actionDeTouche(event, { zoom: cent });
      if (action === null) return;
      event.preventDefault();
      if (action.type === "juger") jugerCourante(action.jugement);
      else if (action.type === "choisir") basculerChoix();
      else voisine(action.type === "precedente" ? -1 : 1);
    };
  });
  useEffect(() => {
    const ecouter = (event: KeyboardEvent): void => clavier.current(event);
    window.addEventListener("keydown", ecouter);
    return () => window.removeEventListener("keydown", ecouter);
  }, []);

  if (courante === undefined) {
    return (
      <>
        <header>
          <h1 className="text-2xl font-semibold">{titre}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Aucune version de cette page n'est encore arrivée : le PC les pousse une fois rendues.
          </p>
        </header>
        <NavigationPages detail={detail} />
      </>
    );
  }

  const choisie = page.choix_version === courante.id;
  const preChoix = page.choix_version === null && page.pre_choix_essai === courante.essai;
  const sousTitre = [
    `Version ${courante.essai} sur ${versions.length}`,
    `graine ${courante.graine}`,
    `${courante.largeur} × ${courante.hauteur}`,
    choisie ? "choisie" : preChoix ? "pré-choix du PC" : null,
  ]
    .filter((morceau) => morceau !== null)
    .join(" · ");

  const statutChoix =
    page.choix_essai !== null
      ? `Choisie pour cette page : version ${page.choix_essai}`
      : page.pre_choix_essai !== null
        ? `Pré-choix du PC : version ${page.pre_choix_essai}`
        : "Pas encore de choix";

  const panneau = (
    <div className="flex min-w-0 flex-col gap-5">
      <Controles controle={courante.controle} />
      <CarteJugement
        basculerChoix={basculerChoix}
        choisie={choisie}
        clos={clos}
        echec={echec}
        image={courante.image}
        jugement={courante.jugement}
        juger={jugerCourante}
        note={note}
        occupe={occupe}
        poserNote={(texte) => setBrouillons((avant) => ({ ...avant, [courante.id]: texte }))}
        statutChoix={statutChoix}
      />
      {mode === "une" ? (
        <VersionsDeLaPage
          afficher={afficher}
          choix={page.choix_version}
          courante={courante.id}
          versions={versions}
        />
      ) : null}
      <PromptDeLaVersion version={courante} />
      <NavigationPages detail={detail} />
    </div>
  );

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold">{titre}</h1>
          <p className="mt-1 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span>{sousTitre}</span>
            {/* Les points sous le titre, sur telephone seulement : sur grand ecran, la carte
                « Versions de la page » dit la meme chose en plus grand. */}
            <span className="md:hidden">
              <PointsDesVersions courante={courante.id} grand versions={versions} />
            </span>
          </p>
        </div>
        <Bascule mode={mode} poser={changerDeMode} />
      </header>

      {mode === "une" ? (
        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,40rem)_minmax(0,1fr)]">
          <ImageEnGrand
            cent={cent}
            key={courante.id}
            poserCent={(oui) => setZoomee(oui ? courante.id : null)}
            titre={titre}
            version={courante}
            voisine={voisine}
          />
          {panneau}
        </div>
      ) : (
        <>
          <CoteACote
            choix={page.choix_version}
            courante={courante.id}
            montrer={(numero) => {
              afficher(numero);
              changerDeMode("une");
            }}
            versions={versions}
          />
          <div className="max-w-3xl">{panneau}</div>
        </>
      )}
    </>
  );
}

/** Une ou toutes : la bascule de l'affichage, sur les ecrans qui ont la place. */
function Bascule({ mode, poser }: { mode: Mode; poser: (mode: Mode) => void }) {
  const options: readonly { mode: Mode; libelle: string }[] = [
    { mode: "une", libelle: "Une à la fois" },
    { mode: "cote", libelle: "Côte à côte" },
  ];
  return (
    <fieldset className="inline-flex overflow-hidden rounded-md border border-input text-sm max-md:hidden">
      <legend className="sr-only">Affichage des versions</legend>
      {options.map((option) => (
        <button
          aria-pressed={mode === option.mode}
          className={cn(
            "border-l border-input px-3 py-1.5 first:border-l-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
            mode === option.mode ? "bg-foreground text-background" : "bg-card hover:bg-muted",
          )}
          key={option.mode}
          onClick={() => poser(option.mode)}
          type="button"
        >
          {option.libelle}
        </button>
      ))}
    </fieldset>
  );
}

/** La largeur a laquelle l'image est affichee, mesuree par le navigateur (pas par jsdom). */
function useLargeurAffichee(image: HTMLImageElement | null): number | null {
  const [largeur, setLargeur] = useState<number | null>(null);
  useEffect(() => {
    if (image === null || typeof ResizeObserver === "undefined") return;
    const observateur = new ResizeObserver(() => setLargeur(image.clientWidth));
    observateur.observe(image);
    return () => observateur.disconnect();
  }, [image]);
  return largeur;
}

/**
 * La version en grand. Un clic la montre a 100 % dans un cadre qui defile, centre sur le
 * point clique (au clavier, sur le milieu) ; un second clic l'ajuste de nouveau a l'ecran.
 * Le zoom est tenu par la page (`cent`) : a 100 %, ses fleches font defiler ce cadre.
 */
function ImageEnGrand({
  version,
  titre,
  voisine,
  cent,
  poserCent,
}: {
  version: VersionDetail;
  titre: string;
  voisine: (pas: -1 | 1) => void;
  cent: boolean;
  poserCent: (cent: boolean) => void;
}) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const cadre = useRef<HTMLDivElement>(null);
  const visee = useRef<{ x: number; y: number } | null>(null);
  const depart = useRef<{ x: number; y: number } | null>(null);
  const apresGlisser = useRef(false);
  const affichee = useLargeurAffichee(image);

  useLayoutEffect(() => {
    const element = cadre.current;
    const point = visee.current;
    if (!cent || element === null || point === null) return;
    element.scrollLeft = point.x * version.largeur - element.clientWidth / 2;
    element.scrollTop = point.y * version.hauteur - element.clientHeight / 2;
    visee.current = null;
  }, [cent, version.largeur, version.hauteur]);

  const basculer = (event: MouseEvent<HTMLButtonElement>): void => {
    if (apresGlisser.current) {
      apresGlisser.current = false;
      return;
    }
    if (!cent) {
      const boite = image?.getBoundingClientRect();
      visee.current =
        event.detail === 0 || boite === undefined || boite.width === 0 || boite.height === 0
          ? { x: 0.5, y: 0.5 }
          : {
              x: (event.clientX - boite.left) / boite.width,
              y: (event.clientY - boite.top) / boite.height,
            };
    }
    poserCent(!cent);
  };

  const toucher = (event: TouchEvent<HTMLButtonElement>): void => {
    apresGlisser.current = false;
    const doigt = event.touches[0];
    depart.current = doigt === undefined ? null : { x: doigt.clientX, y: doigt.clientY };
  };

  const lacher = (event: TouchEvent<HTMLButtonElement>): void => {
    const debut = depart.current;
    depart.current = null;
    const doigt = event.changedTouches[0];
    // A 100 %, le doigt fait defiler l'image : il ne change pas de version.
    if (cent || debut === null || doigt === undefined) return;
    const sens = sensDuGlisser(debut, { x: doigt.clientX, y: doigt.clientY });
    if (sens === null) return;
    apresGlisser.current = true;
    voisine(sens);
  };

  const pourcent =
    affichee === null || affichee === 0 ? null : Math.round((affichee / version.largeur) * 100);
  const legende = cent
    ? "Affichée à 100 %. Un clic l'ajuste à l'écran."
    : `${pourcent === null ? "" : `Affichée à ${pourcent} %. `}Un clic la montre à 100 %.`;

  return (
    <section
      aria-label="Version affichée"
      className="min-w-0 rounded-lg border border-border bg-card p-2.5 text-card-foreground"
    >
      {version.image ? (
        <div className={cn("rounded-md", cent && "max-h-[85vh] overflow-auto")} ref={cadre}>
          <button
            aria-label="Voir à 100 %"
            aria-pressed={cent}
            className={cn(
              "block rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              cent ? "cursor-zoom-out" : "w-full cursor-zoom-in",
            )}
            onClick={basculer}
            onTouchEnd={lacher}
            onTouchStart={toucher}
            type="button"
          >
            <ImageVersion
              alt={`${titre}, version ${version.essai}`}
              className={cent ? "max-w-none" : "h-auto w-full"}
              empreinte={version.empreinte}
              hauteur={version.hauteur}
              imageRef={setImage}
              largeur={version.largeur}
              versionId={version.id}
            />
          </button>
        </div>
      ) : (
        <ImageVersion
          alt=""
          empreinte={version.empreinte}
          hauteur={version.hauteur}
          largeur={version.largeur}
          presente={false}
          versionId={version.id}
        />
      )}
      <p className="px-1.5 pt-2 text-xs text-muted-foreground max-md:hidden">{legende}</p>
      <p className="px-1.5 pt-2 text-xs text-muted-foreground md:hidden">
        Pince pour zoomer, glisse pour changer de version.
      </p>
    </section>
  );
}

/**
 * Une touche du clavier, dessinee. Muette : le bouton porte `aria-keyshortcuts`. Un `span` et
 * non un `kbd`, que Biome prend pour un element focalisable des qu'il est masque.
 */
function Touche({ children }: { children: ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className="rounded border border-border border-b-2 bg-muted px-1.5 font-mono text-xs font-medium text-muted-foreground max-md:hidden"
    >
      {children}
    </span>
  );
}

type Style = { readonly libre: string; readonly presse: string };

/** Le bouton presse est plein, texte en `--card` : meme contraste que le texte sur la carte. */
const STYLE_JUGEMENT: Readonly<Record<Jugement, Style>> = {
  bonne: {
    libre:
      "border-jugement-bonne/50 bg-card text-jugement-bonne enabled:hover:bg-jugement-bonne/15",
    presse: "border-jugement-bonne bg-jugement-bonne text-card",
  },
  defaut: {
    libre:
      "border-jugement-defaut/50 bg-card text-jugement-defaut enabled:hover:bg-jugement-defaut/15",
    presse: "border-jugement-defaut bg-jugement-defaut text-card",
  },
  a_jeter: {
    libre:
      "border-jugement-jeter/50 bg-card text-jugement-jeter enabled:hover:bg-jugement-jeter/15",
    presse: "border-jugement-jeter bg-jugement-jeter text-card",
  },
};

function CarteJugement({
  jugement,
  note,
  poserNote,
  juger,
  basculerChoix,
  choisie,
  statutChoix,
  clos,
  image,
  occupe,
  echec,
}: {
  jugement: Jugement | null;
  note: string;
  poserNote: (texte: string) => void;
  juger: (jugement: Jugement) => void;
  basculerChoix: () => void;
  choisie: boolean;
  statutChoix: string;
  clos: boolean;
  /** Faux tant que l'image n'est pas arrivee : rien ne se juge ni ne se choisit. */
  image: boolean;
  occupe: boolean;
  echec: string | null;
}) {
  const eteints = clos || !image || occupe;
  return (
    <section
      aria-labelledby="coloriage-jugement"
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5 text-card-foreground lg:order-first"
    >
      <h2 className="text-base font-semibold" id="coloriage-jugement">
        Ton jugement
      </h2>
      <textarea
        aria-label="Note"
        className="min-h-16 w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm leading-normal placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        disabled={clos}
        maxLength={500}
        onChange={(event) => poserNote(event.target.value)}
        onKeyDown={(event) => {
          // Echap rend la main au clavier de la page, sans passer par la souris.
          if (event.key === "Escape") event.currentTarget.blur();
        }}
        placeholder="Une note, si besoin"
        rows={2}
        value={note}
      />
      {/* La barre des boutons : dans la carte sur grand ecran, collee en bas de l'ecran sur
          telephone. La marge basse qu'elle impose au corps est dans `theme.css`. */}
      <div className="coloriage-barre flex flex-col gap-2.5 max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:z-30 max-md:border-t max-md:border-border max-md:bg-card max-md:px-4 max-md:pt-3 max-md:pb-4 max-md:shadow-[0_-4px_12px_rgb(0_0_0/0.08)]">
        {clos ? (
          <p className="text-sm text-muted-foreground">
            Ce cahier est clos : ses jugements et ses choix ne se changent plus.
          </p>
        ) : !image ? (
          <p className="text-sm text-muted-foreground">
            L'image de cette version n'est pas encore arrivée : elle se juge et se choisit une fois
            reçue.
          </p>
        ) : null}
        <div className="grid grid-cols-3 gap-2.5">
          {JUGEMENTS.map((valeur) => (
            <button
              aria-keyshortcuts={TOUCHE_JUGEMENT[valeur]}
              aria-pressed={jugement === valeur}
              className={cn(
                "flex items-center justify-center gap-2 rounded-lg border-[1.5px] px-1 py-3 text-sm font-semibold transition-colors max-md:py-3.5",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                "disabled:cursor-not-allowed disabled:opacity-50",
                jugement === valeur ? STYLE_JUGEMENT[valeur].presse : STYLE_JUGEMENT[valeur].libre,
              )}
              disabled={eteints}
              key={valeur}
              onClick={() => juger(valeur)}
              type="button"
            >
              {LIBELLE_JUGEMENT[valeur]}
              <Touche>{TOUCHE_JUGEMENT[valeur]}</Touche>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <Button
            aria-keyshortcuts="C"
            aria-pressed={choisie}
            className="max-md:h-11 max-md:w-full"
            disabled={eteints}
            onClick={basculerChoix}
            variant={choisie ? "default" : "outline"}
          >
            Choisir cette version
            <Touche>C</Touche>
          </Button>
          <span className="text-sm text-muted-foreground max-md:hidden">{statutChoix}</span>
        </div>
        {echec === null ? null : (
          <p className="text-sm text-destructive" role="alert">
            {echec}
          </p>
        )}
      </div>
      <p className="text-xs text-muted-foreground max-md:hidden">
        <Touche>←</Touche> <Touche>→</Touche>
        <span className="sr-only">Flèches gauche et droite :</span> changer de version (à 100 %,
        faire défiler l'image) · après un jugement, la prochaine version à juger s'affiche
      </p>
    </section>
  );
}

/** Les signalements du PC, les erreurs d'abord. */
function Controles({ controle }: { controle: Controle }) {
  const signalements = [
    ...controle.signalements.filter((signalement) => signalement.gravite === "erreur"),
    ...controle.signalements.filter((signalement) => signalement.gravite !== "erreur"),
  ];
  return (
    <section
      aria-labelledby="coloriage-controles"
      className="flex flex-col gap-2.5 rounded-lg border border-border bg-card p-5 text-card-foreground"
    >
      <h2 className="text-base font-semibold" id="coloriage-controles">
        Contrôles automatiques
      </h2>
      {signalements.length === 0 ? (
        <>
          <p className="rounded-md border border-border bg-muted px-3.5 py-2.5 text-sm">
            Rien de mesuré sur cette version.
          </p>
          <p className="text-xs text-muted-foreground">
            Les contrôles voient un cadre, un contour ouvert, un trait gris, un aplat noir, des
            zones trop petites. Ils ne voient ni une patte en trop, ni un sujet faux : c'est ton œil
            qui juge.
          </p>
        </>
      ) : (
        <ul className="flex flex-col gap-2">
          {signalements.map((signalement, rang) => (
            <li
              className={cn(
                "rounded-md border px-3.5 py-2.5 text-sm",
                signalement.gravite === "erreur"
                  ? "border-jugement-jeter/40 bg-jugement-jeter/15 text-jugement-jeter"
                  : "border-jugement-defaut/40 bg-jugement-defaut/15 text-jugement-defaut",
              )}
              // Deux signalements du meme code sont possibles : le rang les distingue.
              // biome-ignore lint/suspicious/noArrayIndexKey: liste figee, jamais reordonnee
              key={`${signalement.code}-${rang}`}
            >
              <span className="font-semibold">
                {signalement.gravite === "erreur" ? "Erreur" : "Avertissement"} :
              </span>{" "}
              {signalement.message}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function VersionsDeLaPage({
  versions,
  courante,
  choix,
  afficher,
}: {
  versions: readonly VersionDetail[];
  courante: number;
  choix: number | null;
  afficher: (numero: number) => void;
}) {
  return (
    <section
      aria-labelledby="coloriage-versions"
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5 text-card-foreground"
    >
      <h2 className="text-base font-semibold" id="coloriage-versions">
        Versions de la page
      </h2>
      <ul className="flex flex-wrap gap-4">
        {versions.map((version) => (
          <li key={version.id}>
            <button
              aria-current={version.id === courante ? "true" : undefined}
              className="flex w-28 flex-col items-start gap-1.5 rounded-md p-1 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => afficher(version.essai)}
              type="button"
            >
              <ImageVersion
                alt=""
                className={cn(
                  "h-auto w-full",
                  version.id === courante && "outline-2 outline-offset-2 outline-foreground",
                )}
                empreinte={version.empreinte}
                hauteur={version.hauteur}
                key={version.id}
                largeur={version.largeur}
                paresseuse
                presente={version.image}
                versionId={version.id}
              />
              <span className="text-xs">
                version {version.essai}
                {version.id === choix ? (
                  <span className="text-muted-foreground"> · choisie</span>
                ) : null}
              </span>
              <BadgeJugement jugement={version.jugement} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Cote a cote : toutes les versions en colonnes ; un clic en montre une, seule. */
function CoteACote({
  versions,
  courante,
  choix,
  montrer,
}: {
  versions: readonly VersionDetail[];
  courante: number;
  choix: number | null;
  montrer: (numero: number) => void;
}) {
  return (
    <section
      aria-label="Versions côte à côte"
      className="grid grid-cols-[repeat(auto-fit,minmax(min(16rem,100%),1fr))] items-start gap-4"
    >
      {versions.map((version) => (
        <button
          aria-current={version.id === courante ? "true" : undefined}
          className={cn(
            "flex flex-col gap-2 rounded-lg border border-input bg-card p-2.5 text-left text-card-foreground hover:border-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            version.id === courante && "outline-2 outline-offset-2 outline-foreground",
          )}
          key={version.id}
          onClick={() => montrer(version.essai)}
          type="button"
        >
          <ImageVersion
            alt=""
            className="h-auto w-full"
            empreinte={version.empreinte}
            hauteur={version.hauteur}
            key={version.id}
            largeur={version.largeur}
            presente={version.image}
            versionId={version.id}
          />
          <span className="flex flex-wrap items-center gap-2 text-sm">
            version {version.essai}
            {version.id === choix ? " · choisie" : ""}
            <BadgeJugement jugement={version.jugement} />
          </span>
        </button>
      ))}
    </section>
  );
}

function PromptDeLaVersion({ version }: { version: VersionDetail }) {
  return (
    <section
      aria-labelledby="coloriage-prompt"
      className="flex flex-col gap-2 rounded-lg border border-border bg-card p-5 text-card-foreground"
    >
      <h2 className="text-base font-semibold" id="coloriage-prompt">
        Prompt de la version
      </h2>
      <p className="text-sm leading-relaxed">{version.prompt}</p>
      <p className="text-xs text-muted-foreground">
        {version.variante === null ? "Prompt de référence" : `Variante « ${version.variante} »`} ·{" "}
        {modeleLong(version.modele)}
      </p>
    </section>
  );
}

/** La page precedente, et la prochaine page qui attend un jugement. */
function NavigationPages({ detail }: { detail: PageDetail }) {
  const { page, precedente, suivante, prochaine_a_juger: prochaine } = detail;
  // La prochaine version a juger peut etre sur cette page meme (les fleches y menent deja).
  const ailleurs = prochaine !== null && prochaine.page !== page.id ? prochaine : null;

  return (
    <nav aria-label="Pages du cahier" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        {precedente === null ? null : (
          <Link
            aria-label={`Page précédente : ${titrePage(precedente.rang, precedente.titre)}`}
            className={LIEN_BOUTON}
            params={{ id: String(precedente.id) }}
            to="/coloriage/pages/$id"
          >
            <span aria-hidden="true">‹</span>
            {titrePage(precedente.rang, precedente.titre)}
          </Link>
        )}
        {ailleurs !== null ? (
          <Link
            className={cn(LIEN_BOUTON_PLEIN, "ml-auto")}
            params={{ id: String(ailleurs.page) }}
            search={{ v: ailleurs.essai }}
            to="/coloriage/pages/$id"
          >
            Prochaine à juger : {titrePage(ailleurs.rang, ailleurs.titre)}
            <span aria-hidden="true">›</span>
          </Link>
        ) : suivante !== null ? (
          <Link
            aria-label={`Page suivante : ${titrePage(suivante.rang, suivante.titre)}`}
            className={cn(LIEN_BOUTON, "ml-auto")}
            params={{ id: String(suivante.id) }}
            to="/coloriage/pages/$id"
          >
            {titrePage(suivante.rang, suivante.titre)}
            <span aria-hidden="true">›</span>
          </Link>
        ) : null}
      </div>
      {prochaine === null ? (
        <p className="text-sm text-muted-foreground">Tout le cahier est jugé.</p>
      ) : null}
    </nav>
  );
}
